import { useEffect, useId, useState } from "react";
import type { Cohort, Invite2, Person2 } from "@/app/peopleQueries";
import { Button } from "@/ui/Button";
import { INPUT_CLS, SELECT_CLS } from "./shared";
import { INVITE_NOTE, inviteReport } from "./model";

export type InviteBody = { emails?: string; csv?: string; role: "student" | "mentor" | "admin"; cohort_id?: string; mentor_id?: string };
export type InviteResult = { created: Invite2[]; skipped: string[] };

const ROLES: { id: InviteBody["role"]; label: string }[] = [
  { id: "student", label: "студент" },
  { id: "mentor", label: "ментор" },
  { id: "admin", label: "админ" },
];

/** Модалка «Пригласить в когорту»: email-ы через запятую или CSV, роль, когорта,
 *  ментор по умолчанию (поровну / конкретный). После отправки — отчёт. */
export function InviteModal({
  open,
  onClose,
  cohorts,
  mentors,
  defaultCohortId,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  cohorts: Cohort[];
  mentors: Person2[];
  defaultCohortId?: string;
  onSubmit: (b: InviteBody) => Promise<InviteResult>;
  pending: boolean;
}) {
  const titleId = useId();
  const [emails, setEmails] = useState("");
  const [csv, setCsv] = useState<{ name: string; text: string }>();
  const [role, setRole] = useState<InviteBody["role"]>("student");
  const [cohortId, setCohortId] = useState(defaultCohortId ?? "");
  const [mentorId, setMentorId] = useState("");
  const [report, setReport] = useState<InviteResult>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (open) {
      setCohortId(defaultCohortId ?? "");
      setReport(undefined);
      setError(undefined);
    }
  }, [open, defaultCohortId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const empty = emails.trim() === "" && !csv;

  const submit = () => {
    if (empty) return;
    setError(undefined);
    const body: InviteBody = { role };
    if (emails.trim()) body.emails = emails.trim();
    if (csv) body.csv = csv.text;
    if (cohortId) body.cohort_id = cohortId;
    if (role === "student" && mentorId) body.mentor_id = mentorId;
    onSubmit(body)
      .then((r) => {
        setReport(r);
        setEmails("");
        setCsv(undefined);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Не удалось отправить приглашения."));
  };

  return (
    <div className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-[8vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="w-[520px] max-w-full overflow-hidden rounded-lg border border-bd2 bg-card text-ink shadow-[0_6px_18px_rgba(0,0,0,.35)]" data-testid="invite-modal">
        <div className="flex items-center gap-3 border-b border-bd bg-bg2 px-[18px] py-3.5">
          <h2 id={titleId} className="m-0 font-mono text-[14px] font-semibold">
            Пригласить в когорту
          </h2>
          <span className="flex-1" />
          <button type="button" aria-label="закрыть" onClick={onClose} className="rounded-sm border border-transparent bg-transparent px-2 py-0.5 font-mono text-[16px] leading-none text-mut hover:text-ink">
            ×
          </button>
        </div>
        <div className="flex flex-col gap-3.5 p-[18px]">
          {report && (
            <div className="rounded-md border border-acc bg-accsoft px-3 py-2.5 font-mono text-[12px] text-acc" role="status" data-testid="invite-report">
              <div>{inviteReport(report.created.length, report.skipped)}</div>
              {report.skipped.length > 0 && (
                <ul className="m-0 mt-1.5 list-none p-0 text-[11px] text-ink2">
                  {report.skipped.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <label className="flex flex-col gap-1.5">
            <span className="font-mono text-[11px] tracking-[.04em] text-mut">email через запятую или файл списком</span>
            <textarea rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="ivanov@mail.ru, petrova@mail.ru" aria-label="email через запятую" className={`${INPUT_CLS} resize-y font-sans text-[13px]`} />
          </label>
          <label className="flex cursor-pointer items-center gap-2.5 rounded-sm border border-dashed border-bd2 px-3 py-2 font-mono text-[11.5px] text-mut hover:border-acc hover:text-ink">
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              aria-label="файл csv со списком"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                void f.text().then((text) => setCsv({ name: f.name, text }));
              }}
            />
            <span>{csv ? `✓ ${csv.name}` : "загрузить csv со списком"}</span>
            {csv && (
              <button type="button" onClick={() => setCsv(undefined)} className="ml-auto border-0 bg-transparent font-mono text-[11px] text-ink2 hover:text-err">
                убрать
              </button>
            )}
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1.5">
              <span className="font-mono text-[11px] tracking-[.04em] text-mut">роль</span>
              <select aria-label="роль" value={role} onChange={(e) => setRole(e.target.value as InviteBody["role"])} className={SELECT_CLS}>
                {ROLES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="font-mono text-[11px] tracking-[.04em] text-mut">когорта</span>
              <select aria-label="когорта" value={cohortId} onChange={(e) => setCohortId(e.target.value)} className={SELECT_CLS}>
                <option value="">без когорты</option>
                {cohorts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="font-mono text-[11px] tracking-[.04em] text-mut">ментор по умолчанию</span>
              <select aria-label="ментор по умолчанию" value={mentorId} disabled={role !== "student"} onChange={(e) => setMentorId(e.target.value)} className={SELECT_CLS}>
                <option value="">распределить поровну</option>
                {mentors.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              {role !== "student" && <span className="font-mono text-[10.5px] text-ink2">ментор есть только у студентов</span>}
            </label>
          </div>
          <p className="m-0 text-[12.5px] leading-[1.5] text-ink2">{INVITE_NOTE}</p>
          {error && (
            <p className="m-0 font-mono text-[11.5px] text-err" role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2.5 border-t border-bd px-[18px] py-3.5">
          <Button variant="ghost" className="text-ink2" onClick={onClose}>
            {report ? "закрыть" : "отмена"}
          </Button>
          <Button variant="primary" data-testid="invite-submit" disabled={empty || pending} reason={empty ? "добавьте хотя бы один email" : pending ? "отправляем…" : undefined} onClick={submit}>
            отправить
          </Button>
        </div>
      </div>
    </div>
  );
}
